// Iter155 — Broker-Truth Reconciliation Widget.
// Self-diagnosing account-margin diff. Consumes:
//   GET  /api/account/broker-truth-reconciliation
//   POST /api/account/broker-truth        (pin broker values)
//   DELETE /api/account/broker-truth      (clear)
//
// Reuses computed values from portfolio.summary (Iter150 married-put
// netting, Iter154 stress margin, Iter155 5.85x PM BP calibration) —
// no margin math lives in this component.

import React from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, CheckCircle2, TrendingUp } from "lucide-react";

const VERDICT_STYLE = {
  aligned:   { color: "#10b981", bg: "rgba(16,185,129,0.08)", label: "aligned" },
  drift:     { color: "#f59e0b", bg: "rgba(245,158,11,0.08)", label: "drift" },
  divergent: { color: "#ef4444", bg: "rgba(239,68,68,0.10)", label: "divergent" },
  no_broker_value: { color: "var(--hoip-text-tertiary)", bg: "transparent", label: "n/a" },
};

const FIELD_LABEL = {
  net_liq: "Net Liquidation Value",
  maintenance_margin: "Maintenance Margin",
  excess_liquidity: "Excess Liquidity",
  buying_power: "Buying Power",
};

function money(v) {
  if (v == null) return "—";
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function BrokerTruthReconciliation() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [form, setForm] = React.useState({
    label: "IBKR",
    net_liq: "",
    maintenance_margin: "",
    excess_liquidity: "",
    buying_power: "",
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/broker-truth-reconciliation");
      setData(data);
      if (data?.broker) {
        setForm({
          label: data.broker.label || "IBKR",
          net_liq: data.broker.net_liq ?? "",
          maintenance_margin: data.broker.maintenance_margin ?? "",
          excess_liquidity: data.broker.excess_liquidity ?? "",
          buying_power: data.broker.buying_power ?? "",
        });
      }
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const save = async () => {
    setLoading(true);
    try {
      await api.post("/account/broker-truth", {
        label: form.label,
        net_liq: form.net_liq === "" ? null : Number(form.net_liq),
        maintenance_margin: form.maintenance_margin === "" ? null : Number(form.maintenance_margin),
        excess_liquidity: form.excess_liquidity === "" ? null : Number(form.excess_liquidity),
        buying_power: form.buying_power === "" ? null : Number(form.buying_power),
      });
      setEditing(false);
      await load();
    } finally { setLoading(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear stored broker-truth values?")) return;
    setLoading(true);
    try {
      await api.delete("/account/broker-truth");
      setData(null);
      setForm({ label: "IBKR", net_liq: "", maintenance_margin: "",
                excess_liquidity: "", buying_power: "" });
    } finally { setLoading(false); }
  };

  if (!data) return null;

  const rows = data.reconciliation || [];
  const largest = data.largest_divergence;

  return (
    <div
      className="border rounded-sm p-3"
      style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      data-testid="broker-truth-reconciliation"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-[13px] font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>
            Broker-Truth Reconciliation
          </span>
          {data.broker?.label && (
            <span className="text-[9px] font-mono-data px-1.5 py-0.5 rounded-sm border"
              style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}
              data-testid="broker-truth-label">
              {data.broker.label} · {data.broker.recorded_at?.slice(0, 10)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-[10px]"
            onClick={() => setEditing(v => !v)}
            data-testid="edit-broker-truth-btn"
          >
            {editing ? "Cancel" : (data.broker ? "Update" : "Pin broker values")}
          </Button>
          {data.broker && (
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px]"
              onClick={clear}
              disabled={loading}
              data-testid="clear-broker-truth-btn"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <div className="grid grid-cols-2 gap-2 mb-3 p-2 rounded-sm"
          style={{ background: "var(--hoip-hover)" }}
          data-testid="broker-truth-editor">
          <label className="text-[10px] col-span-2" style={{ color: "var(--hoip-text-secondary)" }}>
            Label
            <Input
              className="h-6 text-[11px] mt-0.5"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              placeholder="IBKR PM 2026-02"
              data-testid="input-broker-label"
            />
          </label>
          {[
            ["net_liq", "Net Liq"],
            ["maintenance_margin", "Maint Margin"],
            ["excess_liquidity", "Excess Liquidity"],
            ["buying_power", "Buying Power"],
          ].map(([k, lbl]) => (
            <label key={k} className="text-[10px]"
              style={{ color: "var(--hoip-text-secondary)" }}>
              {lbl}
              <Input
                className="h-6 text-[11px] mt-0.5 font-mono-data"
                type="number"
                step="0.01"
                value={form[k]}
                onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                data-testid={`input-broker-${k}`}
              />
            </label>
          ))}
          <div className="col-span-2 flex justify-end">
            <Button size="sm" className="h-6 text-[10px]"
              disabled={loading} onClick={save}
              data-testid="save-broker-truth-btn">
              Save
            </Button>
          </div>
        </div>
      )}

      {largest && (
        <div className="mb-2 flex items-center gap-1.5 text-[10px]">
          <AlertCircle className="w-3 h-3" style={{ color: Math.abs(largest.delta_pct) > 10 ? "#ef4444" : "#f59e0b" }} />
          <span style={{ color: "var(--hoip-text-secondary)" }}>
            Largest divergence: <b>{FIELD_LABEL[largest.field]}</b> · {largest.delta_pct > 0 ? "+" : ""}{largest.delta_pct.toFixed(2)}%
            (our {money(largest.delta > 0 ? "over" : "under")} broker by {money(Math.abs(largest.delta))})
          </span>
        </div>
      )}

      {data.newly_divergent?.length > 0 && (
        <div
          className="mb-2 flex items-start gap-1.5 text-[10px] px-2 py-1.5 rounded-sm border"
          style={{
            background: "rgba(239,68,68,0.08)",
            borderColor: "rgba(239,68,68,0.35)",
            color: "#ef4444",
          }}
          data-testid="newly-divergent-alert"
        >
          <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>
            <b>New divergence detected</b> since your last check:
            {" "}{data.newly_divergent.map(d => FIELD_LABEL[d.field] || d.field).join(", ")}.
            {" "}Broker values likely changed — refresh your pin.
          </span>
        </div>
      )}

      {rows.length > 0 ? (
        <table className="w-full text-[11px] font-mono-data" data-testid="reconciliation-table">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
              <th className="text-left py-1 font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>Field</th>
              <th className="text-right py-1 font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>Ours (computed)</th>
              <th className="text-right py-1 font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>Broker</th>
              <th className="text-right py-1 font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>Δ</th>
              <th className="text-right py-1 font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>Δ%</th>
              <th className="text-right py-1 font-normal" style={{ color: "var(--hoip-text-tertiary)" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const v = VERDICT_STYLE[r.verdict] || VERDICT_STYLE.no_broker_value;
              return (
                <tr key={r.field} data-testid={`reconciliation-row-${r.field}`}>
                  <td className="py-1" style={{ color: "var(--hoip-text-primary)" }}>{FIELD_LABEL[r.field]}</td>
                  <td className="text-right py-1">{money(r.computed)}</td>
                  <td className="text-right py-1">{money(r.broker)}</td>
                  <td className="text-right py-1"
                    style={{ color: r.delta == null ? "var(--hoip-text-tertiary)" :
                                    r.delta > 0 ? "#ef4444" : "#10b981" }}>
                    {r.delta == null ? "—" : (r.delta > 0 ? "+" : "") + money(r.delta)}
                  </td>
                  <td className="text-right py-1">
                    {r.delta_pct == null ? "—" : `${r.delta_pct > 0 ? "+" : ""}${r.delta_pct.toFixed(2)}%`}
                  </td>
                  <td className="text-right py-1">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm"
                      style={{ color: v.color, background: v.bg }}
                      data-testid={`verdict-${r.field}`}>
                      {r.verdict === "aligned" && <CheckCircle2 className="inline w-2.5 h-2.5 mr-0.5" />}
                      {v.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="text-[11px] py-3 text-center" style={{ color: "var(--hoip-text-tertiary)" }}>
          Pin your broker&apos;s latest account values (NLV, maintenance margin, excess
          liquidity, buying power) to see the diff against our computed math.
          Useful for auditing where our stress-margin approximation drifts vs your
          actual broker&apos;s PM engine.
        </div>
      )}

      {data.source_notes?.length > 0 && (
        <details className="mt-2 text-[10px]" data-testid="reconciliation-notes">
          <summary className="cursor-pointer" style={{ color: "var(--hoip-text-tertiary)" }}>
            Why we might diverge ({data.source_notes.length} adjustments)
          </summary>
          <ul className="mt-1 ml-3 list-disc" style={{ color: "var(--hoip-text-secondary)" }}>
            {data.source_notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </details>
      )}

      {data.per_position_margin?.length > 0 && (
        <details className="mt-2 text-[10px]" data-testid="per-position-breakdown">
          <summary className="cursor-pointer" style={{ color: "var(--hoip-text-tertiary)" }}>
            Per-position margin breakdown ({data.per_position_margin.length} lines)
          </summary>
          <table className="w-full mt-1 text-[10px] font-mono-data">
            <thead>
              <tr style={{ color: "var(--hoip-text-tertiary)" }}>
                <th className="text-left font-normal py-0.5">Ticker</th>
                <th className="text-left font-normal py-0.5">Position</th>
                <th className="text-right font-normal py-0.5">App margin</th>
                <th className="text-left font-normal py-0.5 pl-2">Formula</th>
              </tr>
            </thead>
            <tbody>
              {data.per_position_margin.map((p, i) => (
                <tr key={i} style={{ color: "var(--hoip-text-secondary)" }}>
                  <td className="py-0.5" style={{ color: "var(--hoip-text-primary)" }}>{p.ticker}</td>
                  <td className="py-0.5 truncate max-w-[220px]">{p.detail}</td>
                  <td className="py-0.5 text-right">
                    {p.app_margin == null ? "—" : money(p.app_margin)}
                  </td>
                  <td className="py-0.5 pl-2 truncate max-w-[300px]" title={p.notes}>{p.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      <div className="mt-2 text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        {data.buying_power_mult_note}
      </div>
    </div>
  );
}
